// Path: ./src/utils/langPreference.ts
import {
  localStorageSet,
  safeLocalStorageGetItem,
  safeSessionStorageGetItem,
  safeSessionStorageSetItem,
} from "./localStorage";

export const LANG_KEY = "Lang";
export type AppLanguage = "en" | "zh";

const normalizeLanguage = (
  lang: string | null | undefined,
): AppLanguage | null => {
  if (!lang) {
    return null;
  }
  if (lang === "en" || lang.startsWith("en-")) {
    return "en";
  }
  if (lang === "zh" || lang === "zh-CN" || lang.startsWith("zh-")) {
    return "zh";
  }
  return null;
};

const getCookie = (key: string): string | null => {
  try {
    const match = document.cookie.match(
      new RegExp(
        `(?:^|; )${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`,
      ),
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

const setCookie = (key: string, value: string): void => {
  try {
    document.cookie = `${key}=${encodeURIComponent(value)};path=/;SameSite=Lax`;
  } catch {
    // cookies may be blocked in strict privacy contexts
  }
};

export const getLanguagePreference = (): AppLanguage | null => {
  const stored =
    safeLocalStorageGetItem(LANG_KEY) ??
    safeSessionStorageGetItem(LANG_KEY) ??
    getCookie(LANG_KEY);

  return normalizeLanguage(stored);
};

export const setLanguagePreference = (lang: AppLanguage): void => {
  localStorageSet(LANG_KEY, lang);
  safeSessionStorageSetItem(LANG_KEY, lang);
  setCookie(LANG_KEY, lang);
};

export const resolveLanguageFromUrl = (): AppLanguage | null => {
  const langFromUrl = new URLSearchParams(window.location.search).get(
    "language",
  );
  return normalizeLanguage(langFromUrl);
};

export const isEnglishLanguage = (lang: string): boolean =>
  normalizeLanguage(lang) === "en";
